import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2`;

// Risk level colors and labels
const RISK_LEVELS = {
  acceptable: { color: 'bg-green-100 text-green-800 border-green-200', label: 'Akseptabel', bgColor: '#22c55e' },
  tolerable: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', label: 'Tolerabel', bgColor: '#eab308' },
  unacceptable: { color: 'bg-red-100 text-red-800 border-red-200', label: 'Uakseptabel', bgColor: '#ef4444' }
};

function RiskAssessmentPage() {
  const [loading, setLoading] = useState(true);
  const [assessments, setAssessments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [scales, setScales] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showHazardModal, setShowHazardModal] = useState(false);
  const [editingHazard, setEditingHazard] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [assessmentsRes, categoriesRes, scalesRes, industriesRes] = await Promise.all([
        axios.get(`${API}/risk/assessments`, { headers }),
        axios.get(`${API}/risk/categories`, { headers }),
        axios.get(`${API}/risk/scales`, { headers }),
        axios.get(`${API}/risk/industries`, { headers })
      ]);
      
      setAssessments(assessmentsRes.data.assessments || []);
      setCategories(categoriesRes.data.categories || []);
      setScales(scalesRes.data);
      setIndustries(industriesRes.data.industries || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Kunne ikke hente data');
    } finally {
      setLoading(false);
    }
  };

  const loadAssessment = async (assessmentId) => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(`${API}/risk/assessments/${assessmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAssessment(response.data);
    } catch (error) {
      toast.error('Kunne ikke laste risikovurdering');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-800 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-800">
      {/* Header */}
      <header className="bg-slate-900 border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <Link to="/dashboard" className="text-gray-400 hover:text-slate-400">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                </Link>
                <h1 className="text-2xl font-bold text-white">Risikovurdering</h1>
                <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-medium rounded">Kjerneoppgave</span>
              </div>
              <p className="text-sm text-slate-400 mt-1">
                Grunnlaget for hele HMS-systemet ditt
              </p>
            </div>
            {!selectedAssessment && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2"
                data-testid="new-assessment-btn"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Ny risikovurdering
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Introduction for new users */}
        {assessments.length === 0 && !selectedAssessment && (
          <div className="bg-slate-900 rounded-xl border p-8 mb-8">
            <div className="max-w-2xl mx-auto text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Risikovurdering er kjernen i HMS-arbeidet</h2>
              <p className="text-slate-400 mb-6">
                Alt annet i styringssystemet ditt bygger på det du finner her. 
                Prosedyrer, sjekklister og opplæringsplaner - alle skal håndtere 
                risikoer du identifiserer i denne vurderingen.
              </p>
              
              <div className="bg-blue-50 rounded-lg p-4 text-left mb-6">
                <p className="text-sm font-medium text-blue-900 mb-2">Slik fungerer det:</p>
                <ol className="text-sm text-blue-800 space-y-1">
                  <li>1. Velg hvilke fareområder som er relevante for din virksomhet</li>
                  <li>2. Identifiser konkrete farer innenfor hvert område</li>
                  <li>3. Vurder risiko (sannsynlighet × konsekvens)</li>
                  <li>4. Definer tiltak for å redusere risikoen</li>
                  <li>5. Systemet oppretter oppgaver basert på tiltakene</li>
                </ol>
              </div>
              
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
              >
                Start risikovurdering
              </button>
            </div>
          </div>
        )}

        {/* Assessment List */}
        {!selectedAssessment && assessments.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-white">Dine risikovurderinger</h2>
            {assessments.map(assessment => (
              <div
                key={assessment.id}
                onClick={() => loadAssessment(assessment.id)}
                className="bg-slate-900 rounded-xl border p-5 cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-white">{assessment.title}</h3>
                    <p className="text-sm text-slate-400 mt-1">
                      {assessment.total_hazards} farer identifisert • 
                      Opprettet {new Date(assessment.created_at).toLocaleDateString('nb-NO')}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <RiskSummaryBadges assessment={assessment} />
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      assessment.status === 'approved' ? 'bg-green-100 text-green-800' :
                      assessment.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                      'bg-slate-800 text-slate-400'
                    }`}>
                      {assessment.status === 'approved' ? 'Godkjent' :
                       assessment.status === 'completed' ? 'Fullført' :
                       assessment.status === 'in_progress' ? 'Pågår' : 'Utkast'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Selected Assessment Detail */}
        {selectedAssessment && (
          <AssessmentDetail
            assessment={selectedAssessment}
            categories={categories}
            scales={scales}
            onBack={() => setSelectedAssessment(null)}
            onUpdate={loadAssessment}
            onAddHazard={() => {
              setEditingHazard(null);
              setShowHazardModal(true);
            }}
            onEditHazard={(hazard) => {
              setEditingHazard(hazard);
              setShowHazardModal(true);
            }}
          />
        )}
      </main>

      {/* Create Assessment Modal */}
      {showCreateModal && (
        <CreateAssessmentModal
          categories={categories}
          industries={industries}
          onClose={() => setShowCreateModal(false)}
          onCreated={(assessment) => {
            setAssessments([assessment, ...assessments]);
            setSelectedAssessment(assessment);
            setShowCreateModal(false);
          }}
        />
      )}

      {/* Hazard Modal */}
      {showHazardModal && selectedAssessment && (
        <HazardModal
          assessmentId={selectedAssessment.id}
          hazard={editingHazard}
          categories={categories}
          scales={scales}
          industry={selectedAssessment.industry}
          onClose={() => {
            setShowHazardModal(false);
            setEditingHazard(null);
          }}
          onSaved={() => {
            loadAssessment(selectedAssessment.id);
            setShowHazardModal(false);
            setEditingHazard(null);
          }}
        />
      )}
    </div>
  );
}

function RiskSummaryBadges({ assessment }) {
  return (
    <div className="flex items-center gap-2">
      {assessment.unacceptable_count > 0 && (
        <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-medium rounded-full">
          {assessment.unacceptable_count} høy
        </span>
      )}
      {assessment.tolerable_count > 0 && (
        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
          {assessment.tolerable_count} middels
        </span>
      )}
      {assessment.acceptable_count > 0 && (
        <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full">
          {assessment.acceptable_count} lav
        </span>
      )}
    </div>
  );
}

function AssessmentDetail({ assessment, categories, scales, onBack, onUpdate, onAddHazard, onEditHazard }) {
  const [generatingTasks, setGeneratingTasks] = useState(false);

  const generateTasks = async () => {
    setGeneratingTasks(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.post(
        `${API}/risk/assessments/${assessment.id}/generate-tasks`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.created_count > 0) {
        toast.success(`${response.data.created_count} oppgaver opprettet fra tiltak`);
      } else {
        toast.success('Ingen nye oppgaver å opprette');
      }
    } catch (error) {
      toast.error('Kunne ikke opprette oppgaver');
    } finally {
      setGeneratingTasks(false);
    }
  };

  const categoryMap = categories.reduce((acc, c) => ({ ...acc, [c.id]: c }), {});

  // Industry name mapping
  const industryNames = {
    'general': 'Generell',
    'nightlife': 'Utelivsbransjen',
    'construction': 'Bygg og anlegg',
    'office': 'Kontor/Administrasjon',
    'healthcare': 'Helse og omsorg',
    'real_estate': 'Eiendomsbransjen'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-gray-400 hover:text-slate-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-white">{assessment.title}</h2>
              {assessment.industry && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                  {industryNames[assessment.industry] || assessment.industry}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-400">
              {assessment.total_hazards} farer • Sist oppdatert {new Date(assessment.updated_at).toLocaleDateString('nb-NO')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={generateTasks}
            disabled={generatingTasks}
            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            {generatingTasks ? 'Oppretter...' : 'Opprett oppgaver fra tiltak'}
          </button>
          <button
            onClick={onAddHazard}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2"
            data-testid="add-hazard-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Legg til fare
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-slate-900 rounded-xl border p-4">
          <p className="text-sm text-slate-400">Totalt</p>
          <p className="text-2xl font-bold text-white">{assessment.total_hazards}</p>
        </div>
        <div className="bg-red-50 rounded-xl border border-red-100 p-4">
          <p className="text-sm text-red-600">Uakseptabel</p>
          <p className="text-2xl font-bold text-red-700">{assessment.unacceptable_count}</p>
        </div>
        <div className="bg-yellow-50 rounded-xl border border-yellow-100 p-4">
          <p className="text-sm text-yellow-600">Tolerabel</p>
          <p className="text-2xl font-bold text-yellow-700">{assessment.tolerable_count}</p>
        </div>
        <div className="bg-green-50 rounded-xl border border-green-100 p-4">
          <p className="text-sm text-green-600">Akseptabel</p>
          <p className="text-2xl font-bold text-green-700">{assessment.acceptable_count}</p>
        </div>
      </div>

      {/* Risk Matrix Visualization */}
      <div className="bg-slate-900 rounded-xl border p-6">
        <h3 className="font-semibold text-white mb-4">Risikomatrise</h3>
        <RiskMatrix hazards={assessment.hazards || []} scales={scales} />
      </div>

      {/* Hazards List */}
      <div className="bg-slate-900 rounded-xl border">
        <div className="p-4 border-b">
          <h3 className="font-semibold text-white">Identifiserte farer</h3>
        </div>
        {assessment.hazards?.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            <p>Ingen farer lagt til ennå</p>
            <button onClick={onAddHazard} className="mt-2 text-blue-600 hover:underline">
              Legg til første fare
            </button>
          </div>
        ) : (
          <div className="divide-y">
            {assessment.hazards?.map(hazard => (
              <HazardRow
                key={hazard.id}
                hazard={hazard}
                category={categoryMap[hazard.category]}
                onEdit={() => onEditHazard(hazard)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RiskMatrix({ hazards, scales }) {
  // Create 5x5 matrix
  const matrix = Array(5).fill(null).map(() => Array(5).fill(null).map(() => []));
  
  hazards.forEach(h => {
    const likelihoodVal = scales?.likelihood?.find(l => l.id === h.current_likelihood)?.value || 3;
    const consequenceVal = scales?.consequence?.find(c => c.id === h.current_consequence)?.value || 3;
    matrix[5 - likelihoodVal][consequenceVal - 1].push(h);
  });

  const getCellColor = (row, col) => {
    const score = (5 - row) * (col + 1);
    if (score <= 4) return 'bg-green-100';
    if (score <= 12) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="w-24"></th>
            {[1, 2, 3, 4, 5].map(i => (
              <th key={i} className="p-2 text-xs text-slate-400 font-medium text-center border">
                {scales?.consequence?.[i - 1]?.name || i}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[5, 4, 3, 2, 1].map((likelihood, rowIdx) => (
            <tr key={likelihood}>
              <td className="p-2 text-xs text-slate-400 font-medium text-right border">
                {scales?.likelihood?.[likelihood - 1]?.name || likelihood}
              </td>
              {[0, 1, 2, 3, 4].map(col => (
                <td key={col} className={`p-2 border ${getCellColor(rowIdx, col)} min-w-16 h-12 align-middle text-center`}>
                  {matrix[rowIdx][col].length > 0 && (
                    <span className="inline-flex items-center justify-center w-6 h-6 bg-gray-800 text-white text-xs font-bold rounded-full">
                      {matrix[rowIdx][col].length}
                    </span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center justify-center gap-6 mt-4 text-xs text-slate-400">
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-100 rounded"></span> Akseptabel</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-yellow-100 rounded"></span> Tolerabel</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-100 rounded"></span> Uakseptabel</span>
      </div>
    </div>
  );
}

function HazardRow({ hazard, category, onEdit }) {
  const levelConfig = RISK_LEVELS[hazard.current_level] || RISK_LEVELS.tolerable;
  const hasProposedMeasures = hazard.proposed_measures?.length > 0;

  return (
    <div className="p-4 hover:bg-slate-800 cursor-pointer" onClick={onEdit}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${levelConfig.color}`}>
              {levelConfig.label} ({hazard.current_score})
            </span>
            <span className="text-xs text-gray-400">{category?.name || hazard.category}</span>
          </div>
          <p className="font-medium text-white">{hazard.description}</p>
          {hazard.location && (
            <p className="text-sm text-slate-400 mt-1">Sted: {hazard.location}</p>
          )}
          
          {/* Existing measures */}
          {hazard.existing_measures?.length > 0 && (
            <div className="mt-2">
              <p className="text-xs text-slate-400 mb-1">Eksisterende tiltak:</p>
              <div className="flex flex-wrap gap-1">
                {hazard.existing_measures.map((m, i) => (
                  <span key={i} className="text-xs bg-slate-800 text-slate-400 px-2 py-0.5 rounded">
                    {m.description}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Proposed measures (PRO) */}
          {hasProposedMeasures && (
            <div className="mt-2">
              <p className="text-xs text-green-600 mb-1">Foreslåtte tiltak:</p>
              <div className="flex flex-wrap gap-1">
                {hazard.proposed_measures.map((m, i) => (
                  <span key={i} className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded">
                    {m.description}
                  </span>
                ))}
              </div>
              {hazard.target_level && (
                <p className="text-xs text-slate-400 mt-1">
                  Etter tiltak: <span className={RISK_LEVELS[hazard.target_level]?.color.replace('border-', '')}>{RISK_LEVELS[hazard.target_level]?.label} ({hazard.target_score})</span>
                </p>
              )}
            </div>
          )}
        </div>
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
}

function CreateAssessmentModal({ categories, industries, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState('Risikovurdering');
  const [department, setDepartment] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [creating, setCreating] = useState(false);

  const toggleCategory = (catId) => {
    setSelectedCategories(prev => 
      prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]
    );
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.post(`${API}/risk/assessments`, {
        title,
        department: department || null,
        industry: selectedIndustry || null,
        categories: selectedCategories
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Risikovurdering opprettet');
      onCreated(response.data.assessment);
    } catch (error) {
      toast.error('Kunne ikke opprette risikovurdering');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-white">Ny risikovurdering</h2>
          <p className="text-sm text-slate-400 mt-1">
            {step === 1 ? 'Steg 1: Velg din bransje' : 'Steg 2: Velg fareområder'}
          </p>
          {/* Progress indicator */}
          <div className="flex items-center gap-2 mt-3">
            <div className={`h-1.5 flex-1 rounded ${step >= 1 ? 'bg-blue-500' : 'bg-gray-200'}`} />
            <div className={`h-1.5 flex-1 rounded ${step >= 2 ? 'bg-blue-500' : 'bg-gray-200'}`} />
          </div>
        </div>

        <div className="p-6 space-y-6">
          {step === 1 && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Tittel</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="assessment-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Avdeling/område (valgfritt)</label>
                <input
                  type="text"
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  placeholder="F.eks. Produksjon, Kontor, Lager..."
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-3">
                  Hvilken bransje tilhører virksomheten din?
                </label>
                <p className="text-xs text-slate-400 mb-3">
                  Vi har forhåndsdefinerte farer for ulike bransjer for å spare deg tid
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {industries.map(ind => (
                    <button
                      key={ind.id}
                      onClick={() => setSelectedIndustry(ind.id)}
                      className={`p-4 rounded-lg border text-left transition-all ${
                        selectedIndustry === ind.id
                          ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                          : 'border-slate-700 hover:border-slate-600'
                      }`}
                      data-testid={`industry-${ind.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xl">{ind.icon}</span>
                        <span className="font-medium text-white text-sm">{ind.name}</span>
                      </div>
                      <p className="text-xs text-slate-400 line-clamp-2">{ind.description}</p>
                      <p className="text-xs text-blue-600 mt-1">{ind.hazard_count} forhåndsdefinerte farer</p>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-3">Velg fareområder å vurdere</label>
              <p className="text-xs text-slate-400 mb-3">
                Du kan alltid legge til flere områder senere
              </p>
              <div className="grid grid-cols-2 gap-3">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => toggleCategory(cat.id)}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      selectedCategories.includes(cat.id)
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                        : 'border-slate-700 hover:border-slate-600'
                    }`}
                    data-testid={`category-${cat.id}`}
                  >
                    <p className="font-medium text-white text-sm">{cat.name}</p>
                    <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{cat.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t bg-slate-800 flex justify-between">
          <div>
            {step > 1 && (
              <button onClick={() => setStep(step - 1)} className="px-4 py-2 text-slate-300 hover:bg-gray-200 rounded-lg">
                Tilbake
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-slate-300 hover:bg-gray-200 rounded-lg">
              Avbryt
            </button>
            {step === 1 ? (
              <button
                onClick={() => setStep(2)}
                disabled={!title}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
                data-testid="next-step-btn"
              >
                Neste
              </button>
            ) : (
              <button
                onClick={handleCreate}
                disabled={creating || !title}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
                data-testid="create-assessment-btn"
              >
                {creating ? 'Oppretter...' : 'Start vurdering'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function HazardModal({ assessmentId, hazard, categories, scales, industry, onClose, onSaved }) {
  const isEditing = !!hazard;
  const [saving, setSaving] = useState(false);
  const [suggestingMeasures, setSuggestingMeasures] = useState(false);
  const [loadingIndustryHazards, setLoadingIndustryHazards] = useState(false);
  const [industryHazards, setIndustryHazards] = useState([]);
  const [showIndustryPicker, setShowIndustryPicker] = useState(!isEditing && !!industry);
  
  // Form state
  const [category, setCategory] = useState(hazard?.category || '');
  const [description, setDescription] = useState(hazard?.description || '');
  const [location, setLocation] = useState(hazard?.location || '');
  const [affectedPersons, setAffectedPersons] = useState(hazard?.affected_persons || '');
  const [likelihood, setLikelihood] = useState(hazard?.current_likelihood || 'possible');
  const [consequence, setConsequence] = useState(hazard?.current_consequence || 'moderate');
  const [existingMeasures, setExistingMeasures] = useState(hazard?.existing_measures || []);
  const [newMeasure, setNewMeasure] = useState('');
  
  // PRO features
  const [proposedMeasures, setProposedMeasures] = useState(hazard?.proposed_measures || []);
  const [targetLikelihood, setTargetLikelihood] = useState(hazard?.target_likelihood || '');
  const [targetConsequence, setTargetConsequence] = useState(hazard?.target_consequence || '');
  const [showProFeatures, setShowProFeatures] = useState(hazard?.proposed_measures?.length > 0);

  // Load industry hazards when industry is set
  useEffect(() => {
    if (industry && !isEditing) {
      loadIndustryHazards();
    }
  }, [industry, isEditing]);

  const loadIndustryHazards = async () => {
    if (!industry) return;
    setLoadingIndustryHazards(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(`${API}/risk/industries/${industry}/hazards`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIndustryHazards(response.data.hazards || []);
    } catch (error) {
      console.error('Failed to load industry hazards:', error);
    } finally {
      setLoadingIndustryHazards(false);
    }
  };

  const selectPredefinedHazard = (h) => {
    setCategory(h.category);
    setDescription(h.description);
    setLikelihood(h.typical_likelihood);
    setConsequence(h.typical_consequence);
    setExistingMeasures(h.typical_measures.map(m => ({ description: m })));
    setShowIndustryPicker(false);
  };

  const calculateScore = (l, c) => {
    const lVal = scales?.likelihood?.find(x => x.id === l)?.value || 3;
    const cVal = scales?.consequence?.find(x => x.id === c)?.value || 3;
    return lVal * cVal;
  };

  const getLevel = (score) => {
    if (score <= 4) return 'acceptable';
    if (score <= 12) return 'tolerable';
    return 'unacceptable';
  };

  const currentScore = calculateScore(likelihood, consequence);
  const currentLevel = getLevel(currentScore);
  const targetScore = targetLikelihood && targetConsequence ? calculateScore(targetLikelihood, targetConsequence) : null;
  const targetLevel = targetScore ? getLevel(targetScore) : null;

  const addExistingMeasure = () => {
    if (newMeasure.trim()) {
      setExistingMeasures([...existingMeasures, { description: newMeasure.trim() }]);
      setNewMeasure('');
    }
  };

  const suggestMeasures = async () => {
    if (!description || !category) return;
    
    setSuggestingMeasures(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.post(`${API}/risk/ai/suggest-measures`, {
        hazard_description: description,
        category: category,
        current_level: currentLevel
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.suggestions?.length > 0) {
        const newProposed = response.data.suggestions.map(s => ({
          description: s.description,
          status: 'planned',
          creates_task: true
        }));
        setProposedMeasures([...proposedMeasures, ...newProposed]);
        setShowProFeatures(true);
        toast.success('AI foreslo tiltak');
      }
    } catch (error) {
      toast.error('Kunne ikke hente forslag');
    } finally {
      setSuggestingMeasures(false);
    }
  };

  const handleSave = async () => {
    if (!category || !description || !likelihood || !consequence) {
      toast.error('Fyll ut alle påkrevde felt');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      
      if (isEditing) {
        await axios.put(`${API}/risk/assessments/${assessmentId}/hazards/${hazard.id}`, {
          description,
          location: location || null,
          affected_persons: affectedPersons || null,
          current_likelihood: likelihood,
          current_consequence: consequence,
          existing_measures: existingMeasures,
          proposed_measures: proposedMeasures,
          target_likelihood: targetLikelihood || null,
          target_consequence: targetConsequence || null
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/risk/assessments/${assessmentId}/hazards`, {
          category,
          description,
          location: location || null,
          affected_persons: affectedPersons || null,
          current_likelihood: likelihood,
          current_consequence: consequence,
          existing_measures: existingMeasures
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success(isEditing ? 'Fare oppdatert' : 'Fare lagt til');
      onSaved();
    } catch (error) {
      toast.error('Kunne ikke lagre');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-white">{isEditing ? 'Rediger fare' : 'Legg til fare'}</h2>
          {industry && !isEditing && (
            <p className="text-sm text-blue-600 mt-1">
              Vi har forhåndsdefinerte farer for din bransje
            </p>
          )}
        </div>

        <div className="p-6 space-y-5">
          {/* Industry Hazard Picker */}
          {showIndustryPicker && industryHazards.length > 0 && !isEditing && (
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-medium text-blue-900">Typiske farer for din bransje</h3>
                  <p className="text-xs text-blue-700 mt-0.5">Klikk for å fylle ut skjemaet automatisk</p>
                </div>
                <button 
                  onClick={() => setShowIndustryPicker(false)}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Skriv egen
                </button>
              </div>
              {loadingIndustryHazards ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {industryHazards.map((h, idx) => (
                    <button
                      key={idx}
                      onClick={() => selectPredefinedHazard(h)}
                      className="w-full p-3 bg-slate-900 rounded-lg border border-blue-200 text-left hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      data-testid={`industry-hazard-${idx}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-white text-sm">{h.description}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{h.category_name}</p>
                        </div>
                        <div className="ml-2 flex-shrink-0">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            h.typical_likelihood === 'likely' || h.typical_likelihood === 'certain' 
                              ? 'bg-amber-100 text-amber-800' 
                              : 'bg-slate-800 text-slate-400'
                          }`}>
                            {scales?.likelihood?.find(l => l.id === h.typical_likelihood)?.name || h.typical_likelihood}
                          </span>
                        </div>
                      </div>
                      {h.typical_measures.length > 0 && (
                        <p className="text-xs text-green-600 mt-1">
                          Tiltak: {h.typical_measures.slice(0, 2).join(', ')}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Show button to go back to industry picker */}
          {!showIndustryPicker && industry && industryHazards.length > 0 && !isEditing && !description && (
            <button
              onClick={() => setShowIndustryPicker(true)}
              className="w-full p-3 border border-dashed border-blue-300 rounded-lg text-sm text-blue-600 hover:bg-blue-50"
            >
              Vis forhåndsdefinerte farer for din bransje
            </button>
          )}

          {/* Category */}
          {!isEditing && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Kategori *</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                data-testid="hazard-category-select"
              >
                <option value="">Velg kategori</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Beskrivelse av faren *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Hva er faren? Vær konkret."
              className="w-full px-3 py-2 border rounded-lg"
              data-testid="hazard-description-input"
            />
          </div>

          {/* Location & Affected */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Sted/plassering</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Hvor finnes faren?"
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Hvem er utsatt?</label>
              <input
                type="text"
                value={affectedPersons}
                onChange={(e) => setAffectedPersons(e.target.value)}
                placeholder="F.eks. lagerarbeidere"
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>

          {/* Existing Measures */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Eksisterende tiltak/barrierer</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {existingMeasures.map((m, i) => (
                <span key={i} className="inline-flex items-center gap-1 bg-slate-800 text-slate-300 px-2 py-1 rounded text-sm">
                  {m.description}
                  <button onClick={() => setExistingMeasures(existingMeasures.filter((_, idx) => idx !== i))} className="text-gray-400 hover:text-red-500">×</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newMeasure}
                onChange={(e) => setNewMeasure(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addExistingMeasure()}
                placeholder="Legg til eksisterende tiltak"
                className="flex-1 px-3 py-2 border rounded-lg text-sm"
              />
              <button onClick={addExistingMeasure} className="px-3 py-2 bg-slate-800 rounded-lg text-sm hover:bg-gray-200">Legg til</button>
            </div>
          </div>

          {/* Risk Assessment */}
          <div className="bg-slate-800 rounded-lg p-4">
            <h3 className="font-medium text-white mb-3">Risikovurdering (nåværende) *</h3>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Sannsynlighet</label>
                <select value={likelihood} onChange={(e) => setLikelihood(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                  {scales?.likelihood?.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Konsekvens</label>
                <select value={consequence} onChange={(e) => setConsequence(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                  {scales?.consequence?.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className={`text-center py-2 rounded ${RISK_LEVELS[currentLevel]?.color}`}>
              <span className="font-medium">Risikoscore: {currentScore}</span>
              <span className="mx-2">•</span>
              <span>{RISK_LEVELS[currentLevel]?.label}</span>
            </div>
          </div>

          {/* PRO: Target Risk */}
          {currentLevel !== 'acceptable' && (
            <div className="border-t pt-4">
              <button
                onClick={() => setShowProFeatures(!showProFeatures)}
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
              >
                <span>{showProFeatures ? '−' : '+'}</span>
                <span>Definer tiltak og målrisiko (PRO)</span>
              </button>
              
              {showProFeatures && (
                <div className="mt-4 space-y-4">
                  {/* Proposed Measures */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-slate-300">Foreslåtte tiltak</label>
                      <button
                        onClick={suggestMeasures}
                        disabled={suggestingMeasures || !description}
                        className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 disabled:opacity-50"
                      >
                        {suggestingMeasures ? 'Henter forslag...' : '✨ Få AI-forslag'}
                      </button>
                    </div>
                    <div className="space-y-2">
                      {proposedMeasures.map((m, i) => (
                        <div key={i} className="flex items-center gap-2 bg-green-50 p-2 rounded">
                          <input
                            type="text"
                            value={m.description}
                            onChange={(e) => {
                              const updated = [...proposedMeasures];
                              updated[i].description = e.target.value;
                              setProposedMeasures(updated);
                            }}
                            className="flex-1 px-2 py-1 border rounded text-sm"
                          />
                          <button onClick={() => setProposedMeasures(proposedMeasures.filter((_, idx) => idx !== i))} className="text-red-500">×</button>
                        </div>
                      ))}
                      <button
                        onClick={() => setProposedMeasures([...proposedMeasures, { description: '', status: 'planned', creates_task: true }])}
                        className="w-full p-2 border border-dashed rounded-lg text-sm text-slate-400 hover:bg-slate-800"
                      >
                        + Legg til tiltak
                      </button>
                    </div>
                  </div>

                  {/* Target Risk */}
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="font-medium text-white mb-3">Forventet risiko etter tiltak</h3>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Sannsynlighet</label>
                        <select value={targetLikelihood} onChange={(e) => setTargetLikelihood(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                          <option value="">Velg...</option>
                          {scales?.likelihood?.map(l => (
                            <option key={l.id} value={l.id}>{l.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Konsekvens</label>
                        <select value={targetConsequence} onChange={(e) => setTargetConsequence(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                          <option value="">Velg...</option>
                          {scales?.consequence?.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {targetScore && (
                      <div className={`text-center py-2 rounded ${RISK_LEVELS[targetLevel]?.color}`}>
                        <span className="font-medium">Målrisiko: {targetScore}</span>
                        <span className="mx-2">•</span>
                        <span>{RISK_LEVELS[targetLevel]?.label}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-6 border-t bg-slate-800 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-slate-300 hover:bg-gray-200 rounded-lg">Avbryt</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Lagrer...' : isEditing ? 'Oppdater' : 'Legg til fare'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RiskAssessmentPage;
